
import { createContext, useContext, useState, ReactNode, useEffect } from 'react';

interface EarlyAdopterContextType {
  isModalOpen: boolean;
  openModal: (source?: string) => void;
  closeModal: () => void;
  sourcePage: string;
}

const EarlyAdopterContext = createContext<EarlyAdopterContextType | undefined>(undefined);

export const useEarlyAdopter = () => {
  const context = useContext(EarlyAdopterContext);

  if (!context) {
    return {
      isModalOpen: false,
      openModal: () => {},
      closeModal: () => {},
      sourcePage: ''
    };
  }

  return context;
};

interface EarlyAdopterProviderProps {
  children: ReactNode;
}

export const EarlyAdopterProvider = ({ children }: EarlyAdopterProviderProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sourcePage, setSourcePage] = useState('');
  const [userHasClosed, setUserHasClosed] = useState(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  useEffect(() => {
    const checkURLParameters = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const hash = window.location.hash;
      const pathname = window.location.pathname;

      if (pathname !== currentPath) {
        setUserHasClosed(false);
        setCurrentPath(pathname);
      }

      const isDedicatedRoute = pathname === '/early-access' || pathname === '/get-vip' || pathname === '/vip';

      const shouldOpenModal =
        isDedicatedRoute ||
        urlParams.get('modal') === 'early-access' ||
        urlParams.get('signup') === 'true' ||
        urlParams.get('vip') === 'true' ||
        hash === '#early-access' ||
        hash === '#signup' ||
        hash === '#vip';

      if (shouldOpenModal && !isModalOpen && !userHasClosed) {
        const source = determineSource(urlParams, hash, pathname);
        openModal(source);

        if (!isDedicatedRoute) {
          cleanupURL(urlParams, hash);
        }
      }
    };

    const determineSource = (urlParams: URLSearchParams, hash: string, pathname: string) => {
      if (pathname === '/early-access') return 'Direct Route - /early-access';
      if (pathname === '/get-vip') return 'Direct Route - /get-vip';
      if (pathname === '/vip') return 'Direct Route - /vip';
      if (urlParams.get('modal') === 'early-access') return 'Direct Link - Modal Parameter';
      if (urlParams.get('signup') === 'true') return 'Direct Link - Signup Parameter';
      if (urlParams.get('vip') === 'true') return 'Direct Link - VIP Parameter';
      if (hash === '#early-access') return 'Direct Link - Early Access Hash';
      if (hash === '#signup') return 'Direct Link - Signup Hash';
      if (hash === '#vip') return 'Direct Link - VIP Hash';
      return 'Direct Link - Auto Open';
    };

    const cleanupURL = (urlParams: URLSearchParams, hash: string) => {
      let needsCleanup = false;

      if (urlParams.has('modal') || urlParams.has('signup') || urlParams.has('vip')) {
        urlParams.delete('modal');
        urlParams.delete('signup');
        urlParams.delete('vip');
        needsCleanup = true;
      }

      if (hash === '#early-access' || hash === '#signup' || hash === '#vip') {
        needsCleanup = true;
      }

      if (needsCleanup) {
        const newURL = window.location.pathname +
          (urlParams.toString() ? '?' + urlParams.toString() : '') +
          (hash && !['#early-access', '#signup', '#vip'].includes(hash) ? hash : '');

        window.history.replaceState({}, '', newURL);
      }
    };

    checkURLParameters();

    const handlePopState = () => {
      checkURLParameters();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isModalOpen, userHasClosed, currentPath]);

  const openModal = (source = window.location.pathname) => {
    if (isModalOpen) return;

    setSourcePage(source);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setUserHasClosed(true);
  };

  const contextValue = {
    isModalOpen,
    openModal,
    closeModal,
    sourcePage
  };

  return (
    <EarlyAdopterContext.Provider value={contextValue}>
      {children}
    </EarlyAdopterContext.Provider>
  );
};
